// Cross-reference: lifecycle.ts (src/lib/lifecycle.ts) maps the same
// treatment_phase values to staff-facing lifecycle labels (active/alumni/pth/other).
// If phase-to-bucket assignments change here, update lifecycle.ts to match.
import i18n from '@/i18n';
import { msg, resolveMsg, type Msg } from '@/i18n/msg';

export type DashboardMode = 'active' | 'survivorship' | 'memorial';

/**
 * Maps a family's treatment_phase to a dashboard rendering mode.
 * Unknown / missing phases default to 'active' on purpose: we must never
 * accidentally render the Memorial variant for a living patient, nor hide
 * medical surfaces from a family that needs them.
 */
export function resolveDashboardMode(treatmentPhase?: string | null): DashboardMode {
  switch (treatmentPhase) {
    case 'bereaved':
      return 'memorial';
    case 'survivorship':
    case 'off_treatment':
      return 'survivorship';
    default:
      return 'active';
  }
}

/** Which greeting the H1 opens with. The words live in `dashboard:greeting.*`. */
export type GreetingId = 'morning' | 'afternoon' | 'evening' | 'hello';

export interface DashboardHero {
  greeting: GreetingId;
}

/**
 * The greeting keys (#2109 R3 L1). One WHOLE sentence per greeting, with and
 * without the parent's first name, so no language has to accept the English
 * "prefix, name." assembly. The named form carries an `<emphasis>` slot for
 * the serif-italic name (`<Trans components={{ emphasis }}>`).
 */
export const GREETING_NAMED_KEY = {
  morning: 'dashboard:greeting.morning',
  afternoon: 'dashboard:greeting.afternoon',
  evening: 'dashboard:greeting.evening',
  hello: 'dashboard:greeting.hello',
} as const satisfies Record<GreetingId, string>;

export const GREETING_ALONE_KEY = {
  morning: 'dashboard:greeting.morningAlone',
  afternoon: 'dashboard:greeting.afternoonAlone',
  evening: 'dashboard:greeting.eveningAlone',
  hello: 'dashboard:greeting.helloAlone',
} as const satisfies Record<GreetingId, string>;

/**
 * Hero copy for the active and survivorship modes ONLY. Memorial mode must
 * never reach this function — DashboardPage branches to the memorial hero
 * before calling it (locked by DashboardPage.lifecycle.test.tsx), so the
 * time-of-day cheer cannot land on a memorial board. `timeOfDay` is
 * `getTimeOfDay()`.
 *
 * No mode carries a lede. Active lost its summary line on 2026-09-05 (#1721:
 * it repeated a card lower on the page); survivorship lost "Here’s what’s
 * coming up — no rush." on 2026-09-18 (guideline §2.9, Q11: nothing "coming
 * up" is on the page any more — the home is the companion).
 */
export function getDashboardHero(
  mode: DashboardMode,
  timeOfDay: 'morning' | 'afternoon' | 'evening',
): DashboardHero {
  if (mode === 'survivorship') {
    return { greeting: 'hello' };
  }
  return { greeting: timeOfDay };
}

/** The memorial hero as message descriptors (the `memorial` namespace, R3-B7). */
export interface MemorialHeroCopy {
  eyebrow: Msg;
  /** The child's name as written; empty when the record has none. */
  name: string;
  /** Rendered when `name` is empty. */
  titleFallback: Msg;
  lede: Msg;
}

export interface MemorialHero {
  eyebrow: string;
  /** The child's name, rendered plainly as the page title. */
  title: string;
  lede: string;
}

/**
 * Memorial-mode hero: the child's name held plainly. Deliberately static —
 * no time-of-day greeting, no counts, no dynamic summary, no call to action.
 *
 * ── DRAFT COPY — PENDING TRF TONE REVIEW (#644) ──────────────────────────
 * TRF owns the final wording of this surface. These strings shipped ahead of
 * their review by explicit decision (docs/DECISIONS.md 2026-08-11, "The
 * memorial hero is built ahead of TRF's tone review, because an empty
 * suppression is also a tone") — not by oversight. The review request, with
 * this exact copy and alternates, is
 * docs/superpowers/memos/2026-08-11-memorial-copy-review.md.
 * When TRF answers, update the strings in `locales/en/memorial.json` and
 * remove this banner.
 *
 * #2109 R3-B7: the words are the `memorial` namespace. Its keys were held as
 * `draft` in every target language (the PM's force-draft list), because the
 * English is itself unreviewed; Victor reviewed each translation on
 * 2026-09-30 (state `reviewed`), and the banner stays until TRF approves
 * (ruled 2026-10-15).
 */
export function memorialHeroCopy(childName?: string | null): MemorialHeroCopy {
  return {
    eyebrow: msg('memorial:hero.eyebrow'),
    name: (childName ?? '').trim(),
    titleFallback: msg('memorial:hero.titleFallback'),
    lede: msg('memorial:hero.lede'),
  };
}

/**
 * The memorial hero as strings in the ACTIVE language, for callers that are
 * not yet on descriptors (`HealthMemorialView`, lane L3's file). It reads the
 * live i18next language when called; a component that calls it re-renders on
 * a language switch only through its own `useTranslation`. New callers use
 * `memorialHeroCopy` with `useMsg()`.
 */
export function getMemorialHero(childName?: string | null): MemorialHero {
  const copy = memorialHeroCopy(childName);
  const t = i18n.t.bind(i18n);
  return {
    eyebrow: resolveMsg(t, copy.eyebrow),
    title: copy.name || resolveMsg(t, copy.titleFallback),
    lede: resolveMsg(t, copy.lede),
  };
}
